import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useSession } from './hooks/useSession';
import Sidebar from './sidebar';
import Login from './login';
import { UserProvider } from './contexts/UserContext';
import { OrderEntryModalProvider } from './contexts/OrderEntryModalContext';
import { getSafeReturnUrl } from './utils/returnUrl';
import './App.css';

// 画面単位で遅延読み込みし、初期バンドルを小さくする
const Dashboard = lazy(() => import('./dashboard/Dashboard'));
const OrderEntry = lazy(() => import('./order_entry'));
const OrderHistory = lazy(() => import('./order_history'));
const EventExcelDownload = lazy(() => import('./event_excel_download'));
const EventItemEdit = lazy(() => import('./event_item_edit'));
const NewItemApplication = lazy(() => import('./new_item_application.jsx'));
const UserRegistration = lazy(() => import('./user_registration.jsx'));
const Announcements = lazy(() => import('./announcements.jsx'));
const AnnouncementDetail = lazy(() => import('./announcement_detail.jsx'));

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated } = useSession();
  const location = useLocation();
  if (isAuthenticated) return children;
  return <Navigate to="/login" state={{ from: location }} replace />;
}

function LoginRedirect() {
  const location = useLocation();
  return <Navigate to={getSafeReturnUrl(location.state?.from)} replace />;
}

function DashboardLayout() {
  return (
    <ProtectedRoute>
      <OrderEntryModalProvider>
      <Sidebar>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/order-entry" element={<OrderEntry />} />
            <Route path="/order-history" element={<OrderHistory />} />
            <Route path="/event-excel-download" element={<EventExcelDownload />} />
            <Route path="/event-item-edit" element={<EventItemEdit />} />
            <Route path="/new-item-application" element={<NewItemApplication />} />
            <Route path="/user-registration" element={<UserRegistration />} />
            <Route path="/announcements" element={<Announcements />} />
            <Route path="/announcements/:id" element={<AnnouncementDetail />} />
          </Routes>
        </Suspense>
      </Sidebar>
      </OrderEntryModalProvider>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <UserProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginOrRedirect />} />
          <Route path="/dashboard/*" element={<DashboardLayout />} />
          <Route path="/" element={<RootRedirect />} />
        </Routes>
      </Router>
    </UserProvider>
  );
}

function LoginOrRedirect() {
  const { isAuthenticated } = useSession();
  return isAuthenticated ? <LoginRedirect /> : <Login />;
}

function RootRedirect() {
  const { isAuthenticated } = useSession();
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
}

export default App;
